import type { WireColor } from '@/api/types/color'

function channel(value: number | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.min(255, Math.round(value)))
    : null
}

function rgbOf(color: WireColor): [number, number, number] | null {
  const r = channel(color.redLevel)
  const g = channel(color.greenLevel)
  const b = channel(color.blueLevel)

  // The RGB levels win over `hexValue`, which is wrong on some catalog rows.
  if (r != null && g != null && b != null) return [r, g, b]

  const hex = /^#?([0-9a-f]{6})$/i.exec(color.hexValue.trim())?.[1]

  if (!hex) return null

  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number]
}

/**
 * Legacy's tint (`CSWPCColorRef.getTintedValue`, odcolorlibrary.js): a linear mix toward white,
 * so 100 is the full color and 0 is white. Not opacity — that is how backgrounds tint.
 */
function tintChannel(value: number, tint: number): number {
  if (tint >= 0 && tint < 100) return Math.round((255 - value) * (1 - tint / 100) + value)

  return value
}

/** `#rrggbb` for a palette color at `tint` (0–100), or null when the row carries no usable color. */
export function tintedPaletteHex(color: WireColor, tint: number): string | null {
  const rgb = rgbOf(color)

  if (!rgb) return null

  return `#${rgb.map((v) => tintChannel(v, tint).toString(16).padStart(2, '0')).join('')}`
}

/**
 * The color a shape fill should render: its palette `colorId` at `tint` (default 100), or null when
 * there is no id or the palette doesn't know it yet, so the caller falls back to the CSS `fill`.
 */
export function resolvePaletteFill(
  palette: ReadonlyMap<number, WireColor> | null | undefined,
  colorId: number | null | undefined,
  tint: number | null | undefined
): string | null {
  if (colorId == null || !palette) return null

  const color = palette.get(colorId)

  return color ? tintedPaletteHex(color, tint ?? 100) : null
}
